import React from 'react';
import {
  Tag,
  TagLabel,
  TagCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  GridItem,
  Text,
  Box,
  Button,
  VStack,
  HStack,
  IconButton,
  NumberInput,
  NumberInputField,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  InputGroup,
  InputRightElement,
  Icon,
  useColorModeValue
} from '@chakra-ui/react';
import { Select as ChakraSelect } from '@chakra-ui/react';
import { AddIcon, DeleteIcon, ChevronDownIcon, CalendarIcon } from '@chakra-ui/icons';
import CallsForm from './CallsForm';

const LEAD_TAGS = ['Hot', 'Not Responding', 'Fake Lead', 'Inquiring', 'Low Budget', 'Venues Pending', 'Proposal Pending', 'Discuss', 'Corporate', 'Calculator', 'Decor Only', 'Chattarpur Farm', 'Weekend', 'Text Preferred'];

const BasicInfoForm = ({ formData, handleChange, stages, leadSources, users }) => {
  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.700');
  const infoBgColor = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  const inputBgColor = useColorModeValue('white', 'gray.700');

  const handleAddDate = () => {
    const newDates = [...(formData.eventDates || []), ''];
    handleChange('eventDates', newDates);
  };

  const handleDateChange = (index, value) => {
    const newDates = [...(formData.eventDates || [])];
    newDates[index] = value;
    handleChange('eventDates', newDates);
  };

  const handleRemoveDate = (index) => {
    const newDates = [...(formData.eventDates || [])];
    newDates.splice(index, 1);
    handleChange('eventDates', newDates);
  };

  const setFollowUpPreset = (preset) => {
    const now = new Date();
    let followUpDate = new Date(now);
    
    switch(preset) {
      case '2hours':
        followUpDate.setHours(now.getHours() + 2);
        break;
      case '5hours':
        followUpDate.setHours(now.getHours() + 5);
        break;
      case '1day':
        followUpDate.setDate(now.getDate() + 1);
        break;
      case '2days':
        followUpDate.setDate(now.getDate() + 2);
        break;
      case '7days':
        followUpDate.setDate(now.getDate() + 7);
        break;
      default:
        // For custom days
        if (preset.startsWith('custom_')) {
          const days = parseInt(preset.split('_')[1]);
          if (!isNaN(days)) {
            followUpDate.setDate(now.getDate() + days);
          }
        }
        break;
    }
    
    // Format the date to ISO string
    handleChange('nextFollowUpTime', followUpDate.toISOString());
  };

  // Create a reference to the datetime input
  const dateTimeInputRef = React.useRef(null);

  const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInSeconds < 0) {
      // Future date
      const absDiffInSeconds = Math.abs(diffInSeconds);
      const absDiffInMinutes = Math.floor(absDiffInSeconds / 60);
      const absDiffInHours = Math.floor(absDiffInMinutes / 60);
      const absDiffInDays = Math.floor(absDiffInHours / 24);

      if (absDiffInDays > 0) return `in ${absDiffInDays} day${absDiffInDays > 1 ? 's' : ''}`;
      if (absDiffInHours > 0) return `in ${absDiffInHours} hour${absDiffInHours > 1 ? 's' : ''}`;
      if (absDiffInMinutes > 0) return `in ${absDiffInMinutes} minute${absDiffInMinutes > 1 ? 's' : ''}`;
      return 'in less than a minute';
    } else {
      // Past date
      if (diffInDays > 0) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
      if (diffInHours > 0) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
      if (diffInMinutes > 0) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
      return 'just now';
    }
  };

  const handleDateTimeFieldClick = () => {
    if (dateTimeInputRef.current) {
      dateTimeInputRef.current.showPicker();
    }
  };

  const handleDateTimeFieldDoubleClick = (e) => {
    const input = e.currentTarget.querySelector('input[type="datetime-local"]');
    if (input) {
      input.focus();
      input.click();
      
      const rect = input.getBoundingClientRect();
      const event = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: rect.right - 10, 
        clientY: rect.top + rect.height / 2
      });
      input.dispatchEvent(event);
    }
  };

  console.log('Initial Stage Value:', formData.stage);
  console.log('Initial Lead Source Value:', formData.leadSource);

  return (
    <>
      {/* Non-editable fields and lead stage */}
      <GridItem colSpan={2}>
        <Box 
          borderWidth={1} 
          borderRadius="md" 
          p={2} 
          mb={3} 
          bg={infoBgColor}
          borderColor={borderColor}
        >
          <HStack spacing={4} align="flex-start" justify="space-between">
            <HStack spacing={4} align="flex-start">
              <Box>
                <Text fontSize="xs" color={mutedTextColor}>Lead ID</Text>
                <Text fontWeight="medium" fontSize="sm" color={textColor}>
                  {formData.leadId || 'Not assigned yet'}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color={mutedTextColor}>Created</Text>
                <Text fontWeight="medium" fontSize="sm" color={textColor}>
                  {formData.createdAt ? new Date(formData.createdAt).toLocaleString() : 'Not created yet'}
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color={mutedTextColor}>Last Updated</Text>
                <Text fontWeight="medium" fontSize="sm" color={textColor}>
                  {formData.updatedAt ? new Date(formData.updatedAt).toLocaleString() : 'Not updated yet'}
                </Text>
              </Box>
            </HStack>
            <HStack spacing={2}>
              <FormControl width="200px">
                <Text fontSize="xs" color={mutedTextColor} mb={0}>Stage</Text>
                <ChakraSelect
                  size="sm"
                  value={formData.stage?._id || formData.stage || ''}
                  onChange={(e) => handleChange('stage', e.target.value)}
                  placeholder="Select stage"
                  bg={inputBgColor}
                  borderColor={borderColor}
                  color={textColor}
                >
                  {stages.map((stage) => (
                    <option key={stage._id} value={stage._id}>
                      {stage.name}
                    </option>
                  ))}
                </ChakraSelect>
              </FormControl>
              <FormControl width="150px">
                <Text fontSize="xs" color={mutedTextColor} mb={0}>Priority</Text>
                <ChakraSelect
                  size="sm"
                  value={formData.priority || 'medium'}
                  onChange={(e) => handleChange('priority', e.target.value)}
                  bg={inputBgColor}
                  borderColor={borderColor}
                  color={textColor}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </ChakraSelect>
              </FormControl>
            </HStack>
          </HStack>

          {/* Assigned To and Tags in same row */}
          <HStack mt={2} spacing={4} align="flex-start">
            <FormControl flex={1}>
              <Text fontSize="xs" color={mutedTextColor} mb={0}>Assigned To</Text>
              <ChakraSelect
                size="sm"
                value={formData.assignedTo || ''}
                onChange={(e) => handleChange('assignedTo', e.target.value || null)}
                placeholder="Select user"
                bg={inputBgColor}
                borderColor={borderColor}
                color={textColor}
              >
                <option value="">Unassigned</option>
                {users?.map((user) => (
                  <option key={user.uid} value={user.uid}>
                    {user.displayName || user.email}
                  </option>
                ))}
              </ChakraSelect>
            </FormControl>

            <FormControl flex={1}>
              <Text fontSize="xs" color={mutedTextColor} mb={0}>Tags</Text>
              <HStack spacing={1} wrap="wrap">
                {formData.tags?.map((tag, index) => (
                  <Tag
                    key={index}
                    size="sm"
                    borderRadius="full"
                    variant="solid"
                    colorScheme={tag === 'Hot' ? 'red' : 
                                tag === 'Not Responding' ? 'yellow' : 
                                tag === 'Fake Lead' ? 'gray' : 
                                tag === 'Low Budget' ? 'orange' :
                                tag === 'Venues Pending' ? 'purple' :
                                tag === 'Proposal Pending' ? 'teal' :
                                tag === 'Discuss' ? 'cyan' :
                                tag === 'Corporate' ? 'pink' :
                                tag === 'Calculator' ? 'green' :
                                tag === 'Decor Only' ? 'blue' :
                                tag === 'Chattarpur Farm' ? 'brown' :
                                tag === 'Weekend' ? 'orange' :
                                tag === 'Text Preferred' ? 'purple' :
                                'blue'}
                  >
                    <TagLabel>{tag}</TagLabel>
                    <TagCloseButton
                      onClick={() => {
                        const newTags = formData.tags.filter((_, i) => i !== index);
                        handleChange('tags', newTags);
                      }}
                    />
                  </Tag>
                ))}
                <ChakraSelect
                  size="sm"
                  width="auto"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      const newTags = [...(formData.tags || []), e.target.value];
                      handleChange('tags', newTags);
                      e.target.value = ''; // Reset select
                    }
                  }}
                  placeholder="Add tag"
                  bg={inputBgColor}
                  borderColor={borderColor}
                  color={textColor}
                >
                  {LEAD_TAGS.filter(tag => !formData.tags?.includes(tag)).map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </ChakraSelect>
              </HStack>
            </FormControl>
          </HStack>
        </Box>
      </GridItem>

      <GridItem colSpan={2}>
        <FormControl>
          <FormLabel>Next Follow Up Time</FormLabel>
          <HStack>
            <Box 
              flex="1" 
              borderWidth="1px" 
              borderRadius="md" 
              p="1px"
              onClick={handleDateTimeFieldClick}
              cursor="pointer"
              _hover={{ borderColor: "blue.500" }}
              position="relative"
              display="flex"
              alignItems="center"
              bg={inputBgColor}
            >
              <Input
                type="datetime-local"
                ref={dateTimeInputRef}
                value={formData.nextFollowUpTime ? (() => {
                  const date = new Date(formData.nextFollowUpTime);
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  return `${year}-${month}-${day}T${hours}:${minutes}`;
                })() : ''}
                onChange={(e) => handleChange('nextFollowUpTime', e.target.value)}
                border="none"
                _focus={{ boxShadow: "none" }}
                flex="1"
                color={textColor}
              />
              <Icon as={CalendarIcon} mr={2} color="gray.500" />
            </Box>
            <Menu>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
                Preset
              </MenuButton>
              <MenuList>
                <MenuItem onClick={() => setFollowUpPreset('2hours')}>In 2 hours</MenuItem>
                <MenuItem onClick={() => setFollowUpPreset('5hours')}>In 5 hours</MenuItem>
                <MenuItem onClick={() => setFollowUpPreset('1day')}>Tomorrow</MenuItem>
                <MenuItem onClick={() => setFollowUpPreset('2days')}>In 2 days</MenuItem>
                <MenuItem onClick={() => setFollowUpPreset('7days')}>In 1 week</MenuItem>
                <MenuItem onClick={() => setFollowUpPreset('custom_3')}>In 3 days</MenuItem>
                <MenuItem onClick={() => setFollowUpPreset('custom_14')}>In 2 weeks</MenuItem>
              </MenuList>
            </Menu>
          </HStack>
          {formData.nextFollowUpTime && (
            <Text fontSize="xs" color={mutedTextColor} mt={1}>
              Next follow up {getRelativeTime(formData.nextFollowUpTime)}
            </Text>
          )}
        </FormControl>
      </GridItem>

      <GridItem colSpan={2}>
        <HStack spacing={4} align="start">
          <FormControl flex={1}>
            <FormLabel>Phone</FormLabel>
            <Input
              value={formData.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="Enter phone number"
              bg={inputBgColor}
              borderColor={borderColor}
              color={textColor}
            />
          </FormControl>

          <FormControl flex={1}>
            <FormLabel>Email</FormLabel>
            <Input
              value={formData.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="Enter email"
              bg={inputBgColor}
              borderColor={borderColor}
              color={textColor}
            />
          </FormControl>
        </HStack>
      </GridItem>

      <GridItem colSpan={2}>
        <HStack spacing={4} align="start">
          <FormControl flex={1}>
            <FormLabel>Point of Contact</FormLabel>
            <Input
              value={formData.pointOfContact || ''}
              onChange={(e) => handleChange('pointOfContact', e.target.value)}
              placeholder="Enter point of contact"
              bg={inputBgColor}
              borderColor={borderColor}
              color={textColor}
            />
          </FormControl>

          <FormControl flex={1}>
            <FormLabel>Bride and Groom Names</FormLabel>
            <Input
              value={formData.brideAndGroomName || ''}
              onChange={(e) => handleChange('brideAndGroomName', e.target.value)}
              placeholder="Enter bride and groom names"
              bg={inputBgColor}
              borderColor={borderColor}
              color={textColor}
            />
          </FormControl>
        </HStack>
      </GridItem>

      <GridItem colSpan={2}>
        <HStack spacing={4} align="start">
          <FormControl flex={1}>
            <FormLabel>Guest Capacity</FormLabel>
            <NumberInput min={0} value={formData.guestCapacity || 0} onChange={(valueString) => {
              const value = Number(valueString);
              console.log('Guest Capacity Changed:', value);
              handleChange('guestCapacity', value);
            }}>
              <NumberInputField
                placeholder="Enter guest capacity"
                bg={inputBgColor}
                borderColor={borderColor}
                color={textColor}
              />
            </NumberInput>
          </FormControl>

          <FormControl flex={1}>
            <FormLabel>Lead Source</FormLabel>
            <ChakraSelect
              value={formData.leadSource || ''}
              onChange={(e) => {
                console.log('Lead Source Selected:', e.target.value);
                handleChange('leadSource', e.target.value);
              }}
              bg={inputBgColor}
              borderColor={borderColor}
              color={textColor}
            >
              <option value="">Select Lead Source</option>
              {leadSources?.map(source => (
                <option key={source._id} value={source._id}>
                  {source.name}
                </option>
              ))}
            </ChakraSelect>
          </FormControl>
        </HStack>
      </GridItem>

      <GridItem colSpan={2}>
        <FormControl>
          <FormLabel>Preferred Event Dates</FormLabel>
          <VStack align="stretch" spacing={3}>
            {(formData.eventDates || []).map((date, index) => (
              <HStack key={index}>
                <Input
                  type="datetime-local"
                  value={date ? new Date(date).toISOString().slice(0, 16) : ''}
                  onChange={(e) => handleDateChange(index, new Date(e.target.value).toISOString())}
                  bg={inputBgColor}
                  borderColor={borderColor}
                  color={textColor}
                />
                <IconButton
                  icon={<DeleteIcon />}
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => handleRemoveDate(index)}
                  aria-label="Remove date"
                />
              </HStack>
            ))}
            <Button
              leftIcon={<AddIcon />}
              onClick={handleAddDate}
              size="sm"
              colorScheme="blue"
              variant="ghost"
            >
              Add Preferred Date
            </Button>
          </VStack>
        </FormControl>
      </GridItem>

      {/* Call Logs Section */}
      <GridItem colSpan={2}>
        <Box borderWidth={1} borderRadius="md" p={3} mb={4} bg={infoBgColor} borderColor={borderColor}>
          <Text fontSize="md" fontWeight="medium" mb={2} color={textColor}>Call Logs</Text>
          {formData.leadId ? (
            <CallsForm leadId={formData.leadId} onUpdate={() => {}} />
          ) : (
            <Text fontSize="sm" color={mutedTextColor}>Save the lead first to add call logs</Text>
          )}
        </Box>
      </GridItem>

      <GridItem colSpan={2}>
        <FormControl>
          <FormLabel>Customer Notes</FormLabel>
          <Textarea
            value={formData.customerNotes || ''}
            onChange={(e) => handleChange('customerNotes', e.target.value)}
            placeholder="Enter customer notes"
            height="150px"
            bg={inputBgColor}
            borderColor={borderColor}
            color={textColor}
          />
        </FormControl>
      </GridItem>
    </>
  );
};

export default BasicInfoForm;
